import { useEffect } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { logger } from '@parrot-software/observability';

import { AnalyticsStatuses, TrackedPaymentMethods, WizardSteps } from '@subscriptions/presentation/constants';
import type { WizardOrigin } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { resolveWizardOrigin } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { useSubscriptionAnalytics } from '@subscriptions/presentation/hooks';

import {
  CardSavedConfirmation,
  CardUpdatedConfirmation,
  CheckoutErrorConfirmation,
  PaymentDoneConfirmation,
  SpeiConfirmation,
} from './confirmation';

import type { ConfirmationRouteState } from './confirmation';

const paymentLog = logger.forDomain('SUB', { subdomain: 'billing' });

type BlockCreationStepConfirmationProps = {
  blockUuid: string;
};

/**
 * Builds the PII-safe tag set for the `showPaymentConfirmation` event from the
 * confirmation route state. Only the discriminating `variant`, the wizard entry
 * `origin`, the Stripe `mode` (card variants) and the `methodType` (when a payment
 * method is present) are logged — never `providerPublicId` / card digits / CLABE
 * (charter §5).
 *
 * @param state - Confirmation route state pushed by the upstream step
 * @param blockUuid - Subscription block uuid for the in-flight wizard
 * @param origin - Flow the wizard was entered through
 * @returns Tag map for the observability event
 */
const buildConfirmationTags = (
  state: ConfirmationRouteState,
  blockUuid: string,
  origin: WizardOrigin,
): Record<string, string> => {
  const tags: Record<string, string> = {
    'block.uuid': blockUuid,
    origin,
    variant: state.variant,
  };
  if (state.variant !== 'error') {
    tags.methodType = state.paymentMethod.methodType;
  }
  if (state.variant === 'card-saved' || state.variant === 'payment-done') {
    tags.mode = state.mode;
  }
  return tags;
};

/**
 * Variant router for the confirmation step (wizard step 5, terminal).
 *
 * Reads the {@link ConfirmationRouteState} pushed by the upstream steps from
 * `useLocation().state` and switches on `variant` to render the matching outcome:
 * SPEI (implemented here, F1) and the card-saved / payment-done / error variants
 * (placeholders wired for F2/F3 to fill in without touching this switch). A missing
 * route state (e.g. a refresh/deep-link) redirects to the block detail — the route
 * wrapper guards this too, so this is a defensive fallback.
 *
 * The success variants also receive the entry `origin`, resolved from the wizard
 * `?origin=` URL flag, so each picks its own title copy: the "Sucursales agrupadas
 * y ..." wording when the wizard grouped stores, and its own wording when the user
 * came to change a payment method or to reactivate a churned subscription.
 *
 * @param props.blockUuid - Subscription block uuid for the in-flight wizard
 * @returns React element rendering the active confirmation variant
 */
export const BlockCreationStepConfirmation = ({ blockUuid }: BlockCreationStepConfirmationProps) => {
  const { search, state } = useLocation<ConfirmationRouteState | undefined>();
  const variant = state?.variant;
  const origin = resolveWizardOrigin(search);
  const { trackPaymentFinished, trackPaymentStepViewed } = useSubscriptionAnalytics();

  useEffect(() => {
    if (!state) return;
    const paymentMethod = state.variant === 'spei' ? TrackedPaymentMethods.SPEI : TrackedPaymentMethods.CARD;

    paymentLog.info('showPaymentConfirmation', buildConfirmationTags(state, blockUuid, origin));
    trackPaymentStepViewed({ blockUuid, origin, paymentMethod, step: WizardSteps.CONFIRMATION });

    // The checkout already closed the funnel for the variants it produces. SPEI and a saved card
    // reach this screen without passing through it, so their payment leg ends here.
    if (state.variant !== 'spei' && state.variant !== 'card-updated') return;
    trackPaymentFinished({ blockUuid, origin, paymentMethod, status: AnalyticsStatuses.SUCCESS });
    // Fire once per shown confirmation variant — route state is terminal/immutable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockUuid, variant]);

  if (!state) {
    return <Redirect to={`/subscriptions/blocks/${blockUuid}`} />;
  }

  switch (state.variant) {
    case 'spei':
      return <SpeiConfirmation blockUuid={blockUuid} origin={origin} paymentMethod={state.paymentMethod} />;
    case 'card-saved':
      return (
        <CardSavedConfirmation
          blockUuid={blockUuid}
          mode={state.mode}
          origin={origin}
          paymentMethod={state.paymentMethod}
        />
      );
    case 'payment-done':
      return (
        <PaymentDoneConfirmation
          blockUuid={blockUuid}
          chargedBlock={state.chargedBlock}
          mode={state.mode}
          origin={origin}
          paymentMethod={state.paymentMethod}
        />
      );
    case 'card-updated':
      return <CardUpdatedConfirmation blockUuid={blockUuid} origin={origin} paymentMethod={state.paymentMethod} />;
    case 'error':
      return <CheckoutErrorConfirmation blockUuid={blockUuid} errorCode={state.errorCode} />;
    default:
      return <Redirect to={`/subscriptions/blocks/${blockUuid}`} />;
  }
};
